import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ShoppingCart, Activity, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PurchaseInfoModal = ({ onClose, wonum }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wonum) return;
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`/api/workorder/${encodeURIComponent(wonum)}/purchase-info`);
        setInfo(res.data);
      } catch (err) {
        console.error("Failed to fetch purchase info", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [wonum]);

  const renderTable = (title, items, columns) => (
    <div className="mb-6">
      <h3 className={`text-xs font-black text-ink-secondary uppercase tracking-widest mb-2 flex items-center gap-2 `}>
        <FileText size={14} /> {title} ({items.length})
      </h3>
      <div className="border rounded-lg overflow-hidden bg-surface-card shadow-sm">
        <table className={`w-full text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
          <thead className="bg-surface-panel text-ink-secondary font-black text-[10px] uppercase">
            <tr>
              {columns.map((col, idx) => <th key={idx} className="p-2 border-b">{col.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y text-xs font-bold text-primary">
            {items.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-4 text-center text-ink-secondary italic">{t('wo.purchase_info.no_records')}</td></tr>
            ) : (
              items.map((item, idx) => (
                <tr key={idx} className="hover:bg-primary-soft transition-colors">
                  {columns.map((col, cIdx) => (
                    <td key={cIdx} className="p-2">
                      {col.key === 'status' ? (
                        <span className="px-2 py-0.5 bg-accent-soft text-accent rounded-full text-[9px]">{item[col.key]}</span>
                      ) : (
                        item[col.key] || '---'
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-surface-card w-full max-w-3xl rounded-lg shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 h-[80vh] ${isRTL ? 'text-right' : 'text-left'}`}>

        {/* Header */}
        <div className={`bg-primary-dark p-4 flex justify-between items-center text-ink-inverse `}>
          <div className={`flex items-center gap-3 `}>
            <ShoppingCart className="text-accent" />
            <h2 className="text-sm font-black uppercase tracking-widest">
              {t('wo.purchase_info.title')} - {wonum}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-ink-inverse/20 p-1 rounded transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-surface-panel flex-1 overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 opacity-50">
              <Activity className="animate-spin mb-2" size={32} />
              <span className="text-xs font-black uppercase tracking-widest">{t('wo.purchase_info.fetching')}</span>
            </div>
          ) : info ? (
            <>
              {renderTable(t('wo.purchase_info.mr_title'), info.material_requests, [
                { label: t('wo.purchase_info.mr_num'), key: "mrnum" },
                { label: t('wo.purchase_info.status'), key: "status" },
                { label: t('wo.purchase_info.req_date'), key: "date" }
              ])}

              {renderTable(t('wo.purchase_info.pr_title'), info.purchase_requests, [
                { label: t('wo.purchase_info.pr_num'), key: "prnum" },
                { label: t('wo.purchase_info.status'), key: "status" },
                { label: t('wo.purchase_info.total_cost'), key: "total" }
              ])}

              {renderTable(t('wo.purchase_info.po_title'), info.purchase_orders, [
                { label: t('wo.purchase_info.po_num'), key: "ponum" },
                { label: t('wo.purchase_info.vendor'), key: "vendor" },
                { label: t('wo.purchase_info.status'), key: "status" },
                { label: t('wo.purchase_info.total_cost'), key: "total" }
              ])}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PurchaseInfoModal;
